import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Theme,
  Tooltip,
  Typography
} from '@mui/material';
import { darken } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useFlags } from '../../hooks';
import { FC, Fragment, useContext, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { UserContext } from '../../context';
import {
  getRelativeLink,
  hasNavAccess,
  hasNavActiveFlags,
  isExternalLink,
  isExternalUser
} from '../../helpers';
import { INavLink, UserRoles } from '../../models';
import { ExternalLink } from '../link/ExternalLink';

export const NestedItem: FC<{
  link: INavLink;
  isNavOpen: boolean;
  showText: boolean;
  navOpen?: any;
  setOpenNav?: any;
  isAnyVendorConfigured?: boolean;
}> = ({ link, isNavOpen, showText, navOpen, setOpenNav, isAnyVendorConfigured }) => {
  const { pathname } = useLocation();
  const userContext = useContext(UserContext);
  const history = useHistory();
  const classes = useStyles({ isNavOpen });
  const featureFlags = useFlags();

  const onlyHasBLClosing =
    userContext?.user?.userRoles?.length === 1 &&
    userContext?.user?.userRoles?.includes(UserRoles.BLClosing);

  const [subNavOpen, setOpenSubNav] = useState<Record<string, boolean>>({
    'Manage Tools': pathname.includes('manage/') ? true : false,
    'Build Tools': pathname.includes('build/') ? true : false,
    'Financial Tools': pathname.includes('financial/') ? true : false,
    'Content Management': pathname.includes('manage-content/') ? true : false,
    Orders: pathname.includes('orders/') ? true : false,
    Checklists: pathname.includes('checklists/') ? true : false,
    Punchlists: pathname.includes('punchlists/') ? true : false,
    'Field Walkthroughs':
      pathname.includes('/field-walkthroughs') || pathname.includes('field-walkthroughs/')
        ? true
        : false,
    Contracts: pathname.includes('vendors/contracts') ? true : false,
    Plans: pathname.includes('development/plans') ? true : false,
    Possessions: pathname.includes('development/possessions') ? true : false,
    Options: pathname.includes('development/options') ? true : false,
    Parts: pathname.includes('development/parts') ? true : false,
    'Vendor Tools': pathname.includes('vendor-tools/') ? true : false,
    'User Tools': pathname.includes('user-tools/') ? true : false,
    //Style Guide
    Components: pathname.includes('kitchen-sink/components/') ? true : false,
    Buttons: pathname.includes('kitchen-sink/components/buttons') ? true : false,
    Grids: pathname.includes('kitchen-sink/components/grids') ? true : false,
    Dashboard: pathname.includes('sales/dashboard') ? true : false,
    Contracting: pathname.includes('sales/contracting') ? true : false,
    Tools: pathname.includes('sales/sales-tools') ? true : false,
    'Build Documentation': pathname.includes('documentation') ? true : false,
    'Unified User Management': pathname.includes(
      'admin/user-tools/unified-user-management/manage-roles-and-titles'
    )
      ? true
      : false
  });

  const isActive = (pathname: string, linkUrl: string, level: number) => {
    const splitPathname = pathname?.split?.('/').filter(Boolean);
    const splitLinkUrl = linkUrl?.split?.('/').filter(Boolean);

    const isDetail = Number(splitPathname[splitPathname.length - 1]) > 0;
    const detailRoot = pathname.substring(0, pathname.lastIndexOf('/'));

    // If not a detail view and the link URL has more segments than the current path,
    // then this link cannot be active.
    if (!isDetail && splitLinkUrl?.length > splitPathname?.length) {
      return false;
    }

    const doesFirstLevelMatch = splitPathname?.[0] === splitLinkUrl?.[0];
    const doesSecondLevelMatch = splitPathname?.[1] === splitLinkUrl?.[1];
    const doesThirdLevelMatch = splitPathname?.[2] === splitLinkUrl?.[2];
    const doesFourthLevelMatch = splitPathname?.[3] === splitLinkUrl?.[3];

    const isKitchenSink = splitPathname?.[0] === 'kitchen-sink'; // Kitchen sink nav is 1 level off from normal nav

    const isKitchenSinkItemActive =
      isKitchenSink &&
      ((level === 1 && doesFirstLevelMatch && doesSecondLevelMatch) ||
        (level === 2 && doesFirstLevelMatch && doesSecondLevelMatch && doesThirdLevelMatch) ||
        (level === 3 &&
          doesFirstLevelMatch &&
          doesSecondLevelMatch &&
          doesThirdLevelMatch &&
          doesFourthLevelMatch));

    const isNavItemActive =
      !isKitchenSink &&
      ((isDetail && linkUrl === detailRoot) ||
        (level === 1 && doesFirstLevelMatch) ||
        (level === 2 && doesFirstLevelMatch && doesSecondLevelMatch) ||
        (level === 3 && doesFirstLevelMatch && doesSecondLevelMatch && doesThirdLevelMatch));

    if (isKitchenSinkItemActive || isNavItemActive) {
      return true;
    }
    return false;
  };

  if (hasNavActiveFlags(link, featureFlags)) {
    return (
      <>
        <Tooltip title={!isNavOpen ? link.name : ''} placement="right-end">
          <ListItem
            button
            onClick={() => {
              setOpenNav({ [link.name]: !navOpen[link.name] });
            }}
            className={classes.listItem}
          >
            {link.icon && (
              <ListItemIcon
                onClick={() => history.push(link?.url)}
                className={clsx(
                  classes.listItemIcon,
                  isActive(pathname, link.url, 1) && classes.activeIcon
                )}
                classes={{ root: classes.listItemIconRoot }}
              >
                {link.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={
                isExternalLink(link?.url) ? (
                  <ExternalLink to={link.url} className={classes.drawerLink} target="_blank">
                    {showText ? link.name : ''}
                  </ExternalLink>
                ) : link?.url ? (
                  <Link
                    to={getRelativeLink(link.url)}
                    className={clsx(
                      classes.drawerLink,
                      isActive(pathname, link.url, 1) && 'active'
                    )}
                  >
                    {showText ? link.name : ''}
                  </Link>
                ) : (
                  <Typography>{showText ? link.name : ''}</Typography>
                )
              }
              className={classes.listItemTextTopLevel}
            />
            {link?.links
              ?.filter(link =>
                featureFlags?.authorizeUserNavigationAccess
                  ? true
                  : hasNavAccess(link.roles, userContext)
              )
              .filter(link => !!hasNavActiveFlags(link, featureFlags))
              .filter(link => !(link.isInternalOnly && isExternalUser(userContext?.user)))
              .filter(link => !(link.name === 'User Management' && !isAnyVendorConfigured)).length >
              0 && <>{navOpen[link.name] ? <ExpandLess /> : <ExpandMore />}</>}
          </ListItem>
        </Tooltip>
        <Collapse in={navOpen[link.name]} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {link?.links &&
              link?.links
                .filter(link =>
                  featureFlags?.authorizeUserNavigationAccess
                    ? true
                    : hasNavAccess(link.roles, userContext)
                )
                .filter(item => !!hasNavActiveFlags(item, featureFlags))
                .filter(item => !(item.name === 'User Management' && !isAnyVendorConfigured))
                .filter(link => !(link.isInternalOnly && isExternalUser(userContext?.user)))
                .map((subLink, index) => {
                  const hasSubLinks =
                    subLink?.links &&
                    subLink.links
                      .filter(item => !!hasNavActiveFlags(item, featureFlags))
                      .filter(link =>
                        featureFlags?.authorizeUserNavigationAccess
                          ? true
                          : hasNavAccess(link.roles, userContext) &&
                            !!hasNavActiveFlags(link, featureFlags) &&
                            !(link.isInternalOnly && isExternalUser(userContext?.user))
                      ).length > 0;
                  const hasLegacyLinkWithFFOff =
                    subLink.featureFlag && subLink.legacyUrl && !featureFlags[subLink.featureFlag]
                      ? true
                      : false;
                  return (
                    <Fragment key={`${index}`}>
                      {isExternalLink(subLink?.url) || hasLegacyLinkWithFFOff ? (
                        <ExternalLinkItem
                          pathname={pathname}
                          isNavOpen={isNavOpen}
                          link={{
                            ...subLink,
                            name:
                              onlyHasBLClosing && subLink.name === 'Build Documentation'
                                ? 'Closing'
                                : subLink.name,
                            url: hasLegacyLinkWithFFOff ? subLink?.legacyUrl : subLink?.url
                          }}
                          showText={showText}
                          isActive={isActive}
                          activeLevel={2}
                        />
                      ) : (
                        <InternalLinkItem
                          pathname={pathname}
                          isNavOpen={isNavOpen}
                          link={subLink}
                          showText={showText}
                          isActive={isActive}
                          // Now, if there are sublinks OR no URL is provided, treat it as a container.
                          hasTopLevelLink={hasSubLinks || !subLink?.url}
                          expandMore={
                            hasSubLinks &&
                            (subNavOpen[subLink.name] ? <ExpandLess /> : <ExpandMore />)
                          }
                          setOpenSubNav={setOpenSubNav}
                          subNavOpen={subNavOpen}
                          activeLevel={2}
                        />
                      )}
                      {/* Updated block: Check for nested links within subLink.links */}
                      {subLink?.links && (
                        <Collapse in={subNavOpen[subLink.name]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {subLink.links
                              .filter(link =>
                                featureFlags?.authorizeUserNavigationAccess
                                  ? true
                                  : hasNavAccess(link.roles, userContext)
                              )
                              .filter(link => !!hasNavActiveFlags(link, featureFlags))
                              .filter(
                                link => !(link.isInternalOnly && isExternalUser(userContext?.user))
                              )
                              .map((childLink, childIndex) => {
                                const hasLegacyLinkWithFFOff =
                                  childLink.featureFlag &&
                                  childLink.legacyUrl &&
                                  !featureFlags[childLink.featureFlag]
                                    ? true
                                    : false;
                                // Determine if this child link has its own nested links
                                const childHasSubLinks =
                                  childLink?.links &&
                                  childLink.links
                                    .filter(item => !!hasNavActiveFlags(item, featureFlags))
                                    .filter(link =>
                                      !!featureFlags?.authorizeUserNavigationAccess
                                        ? true
                                        : hasNavAccess(link.roles, userContext) &&
                                          !!hasNavActiveFlags(link, featureFlags) &&
                                          !(
                                            link.isInternalOnly && isExternalUser(userContext?.user)
                                          )
                                    ).length > 0;
                                return (
                                  <Fragment key={`${childIndex}`}>
                                    {!childLink?.url ||
                                    isExternalLink(childLink?.url) ||
                                    hasLegacyLinkWithFFOff ? (
                                      <ExternalLinkItem
                                        pathname={pathname}
                                        isNavOpen={isNavOpen}
                                        link={{
                                          ...childLink,
                                          url:
                                            hasLegacyLinkWithFFOff || !childLink?.url
                                              ? childLink?.legacyUrl
                                              : childLink?.url
                                        }}
                                        showText={showText}
                                        isActive={isActive}
                                        isSubNested
                                        activeLevel={3}
                                      />
                                    ) : childHasSubLinks ? (
                                      <InternalLinkItem
                                        pathname={pathname}
                                        isNavOpen={isNavOpen}
                                        link={childLink}
                                        showText={showText}
                                        isActive={isActive}
                                        isSubNested
                                        activeLevel={3}
                                        hasTopLevelLink
                                        setOpenSubNav={setOpenSubNav}
                                        subNavOpen={subNavOpen}
                                        expandMore={
                                          subNavOpen[childLink.name] ? (
                                            <ExpandLess />
                                          ) : (
                                            <ExpandMore />
                                          )
                                        }
                                      />
                                    ) : (
                                      <InternalLinkItem
                                        pathname={pathname}
                                        isNavOpen={isNavOpen}
                                        link={childLink}
                                        showText={showText}
                                        isActive={isActive}
                                        isSubNested
                                        activeLevel={3}
                                      />
                                    )}
                                    {/* If this child link has further nested links, render them recursively */}
                                    {childLink?.links && (
                                      <Collapse
                                        in={subNavOpen[childLink.name]}
                                        timeout="auto"
                                        unmountOnExit
                                      >
                                        <List component="div" disablePadding>
                                          {childLink.links
                                            .filter(link =>
                                              !!featureFlags?.authorizeUserNavigationAccess
                                                ? true
                                                : hasNavAccess(link.roles, userContext)
                                            )
                                            .filter(link => !!hasNavActiveFlags(link, featureFlags))
                                            .filter(
                                              link =>
                                                !(
                                                  link.isInternalOnly &&
                                                  isExternalUser(userContext?.user)
                                                )
                                            )
                                            .map((grandChildLink, grandChildIndex) => {
                                              const hasLegacyLinkWithFFOff =
                                                grandChildLink.featureFlag &&
                                                grandChildLink.legacyUrl &&
                                                !featureFlags[grandChildLink.featureFlag]
                                                  ? true
                                                  : false;
                                              return (
                                                <Fragment key={`${grandChildIndex}`}>
                                                  {!grandChildLink?.url ||
                                                  isExternalLink(grandChildLink?.url) ||
                                                  hasLegacyLinkWithFFOff ? (
                                                    <ExternalLinkItem
                                                      pathname={pathname}
                                                      isNavOpen={isNavOpen}
                                                      link={{
                                                        ...grandChildLink,
                                                        url:
                                                          hasLegacyLinkWithFFOff ||
                                                          !grandChildLink?.url
                                                            ? grandChildLink?.legacyUrl
                                                            : grandChildLink?.url
                                                      }}
                                                      showText={showText}
                                                      isActive={isActive}
                                                      isSubNested
                                                      activeLevel={4}
                                                    />
                                                  ) : (
                                                    <InternalLinkItem
                                                      pathname={pathname}
                                                      isNavOpen={isNavOpen}
                                                      link={grandChildLink}
                                                      showText={showText}
                                                      isActive={isActive}
                                                      isSubNested
                                                      activeLevel={4}
                                                    />
                                                  )}
                                                </Fragment>
                                              );
                                            })}
                                        </List>
                                      </Collapse>
                                    )}
                                  </Fragment>
                                );
                              })}
                          </List>
                        </Collapse>
                      )}
                    </Fragment>
                  );
                })}
          </List>
        </Collapse>
      </>
    );
  } else {
    return null;
  }
};

interface IListItemContent {
  pathname: string;
  isNavOpen: boolean;
  link: INavLink;
  showText: boolean;
  isActive: (pathname: string, url: string, number) => boolean;
  expandMore?: JSX.Element;
  isSubNested?: boolean;
  handleButtonClick?: () => void;
  activeLevel: number;
  hasTopLevelLink?: boolean;
}

const ListItemContent: FC<IListItemContent> = ({
  pathname,
  isNavOpen,
  isActive,
  link,
  showText,
  isSubNested,
  handleButtonClick,
  expandMore,
  activeLevel,
  hasTopLevelLink
}) => {
  const classes = useStyles({ isNavOpen });
  const indentClass =
    activeLevel === 4 ? classes.subSubNested : isSubNested ? classes.subNested : classes.nested;
  return (
    <Tooltip title={!isNavOpen ? link.name : ''} placement="right-end">
      <ListItem button className={indentClass} onClick={handleButtonClick}>
        <ListItemIcon
          className={clsx(
            classes.listItemIcon,
            isActive(pathname, link.url, activeLevel) && classes.activeIcon
          )}
          classes={{ root: classes.listItemIconRoot }}
        >
          {link.icon}
        </ListItemIcon>
        <ListItemText
          primary={
            hasTopLevelLink ? (
              <>
                {isExternalLink(link?.url) ? (
                  <ExternalLink to={link.url} className={classes.drawerLink} target="_blank">
                    {showText ? link.name : ''}
                  </ExternalLink>
                ) : link?.url ? (
                  <Link
                    to={getRelativeLink(link.url)}
                    className={clsx(
                      classes.drawerLink,
                      isActive(pathname, link.url, activeLevel) && 'active'
                    )}
                  >
                    {showText ? link.name : ''}
                  </Link>
                ) : (
                  <Typography>{showText ? link.name : ''}</Typography>
                )}
              </>
            ) : showText ? (
              link.name
            ) : (
              ''
            )
          }
          className={activeLevel === 2 ? classes.listItemTextTopLevel : classes.subListItemText}
        />
        {expandMore}
      </ListItem>
    </Tooltip>
  );
};

interface IExternalLinkItem {
  pathname: string;
  isNavOpen: boolean;
  link: INavLink;
  showText: boolean;
  isActive: (pathname: string, url: string, number) => boolean;
  expandMore?: JSX.Element;
  isSubNested?: boolean;
  activeLevel: number;
}

const ExternalLinkItem: FC<IExternalLinkItem> = ({
  pathname,
  isNavOpen,
  isActive,
  link,
  showText,
  isSubNested,
  activeLevel
}) => {
  const classes = useStyles({ isNavOpen });
  return (
    <ExternalLink to={link.url} className={classes.drawerLink} target="_blank">
      <ListItemContent
        pathname={pathname}
        isNavOpen={isNavOpen}
        link={link}
        showText={showText}
        isActive={isActive}
        isSubNested={isSubNested}
        activeLevel={activeLevel}
      />
    </ExternalLink>
  );
};

interface IInternalLinkItem {
  pathname: string;
  isNavOpen: boolean;
  link: INavLink;
  showText: boolean;
  isActive: (pathname: string, url: string, number) => boolean;
  expandMore?: JSX.Element;
  isSubNested?: boolean;
  hasTopLevelLink?: boolean;
  setOpenSubNav?: (val: { [val: string]: boolean }) => void;
  subNavOpen?: { [val: string]: boolean };
  activeLevel: number;
}

const InternalLinkItem: FC<IInternalLinkItem> = ({
  pathname,
  isNavOpen,
  isActive,
  link,
  showText,
  expandMore,
  isSubNested,
  hasTopLevelLink,
  subNavOpen,
  setOpenSubNav,
  activeLevel
}) => {
  const classes = useStyles({ isNavOpen });
  return hasTopLevelLink ? (
    <ListItemContent
      pathname={pathname}
      isNavOpen={isNavOpen}
      link={link}
      showText={showText}
      hasTopLevelLink={hasTopLevelLink}
      isActive={isActive}
      isSubNested={isSubNested}
      handleButtonClick={() => {
        setOpenSubNav({
          ...subNavOpen,
          [link.name]: !subNavOpen[link.name]
        });
      }}
      expandMore={expandMore}
      activeLevel={activeLevel}
    />
  ) : (
    <Link
      to={getRelativeLink(link.url)}
      className={clsx(classes.drawerLink, isActive(pathname, link.url, 3) && 'active')}
    >
      <ListItemContent
        pathname={pathname}
        isNavOpen={isNavOpen}
        link={link}
        showText={showText}
        isActive={isActive}
        isSubNested={isSubNested}
        activeLevel={activeLevel}
        expandMore={expandMore}
      />
    </Link>
  );
};

const useStyles = makeStyles<Theme, { isNavOpen: boolean }>((theme: Theme) => ({
  nested: {
    paddingLeft: 25
  },
  subNested: {
    paddingLeft: 40
  },
  subSubNested: {
    paddingLeft: 55
  },
  listItem: {
    '&:hover': {
      '&& a': {
        color: theme.palette.primary.dark
      }
    }
  },
  listItemText: {
    marginLeft: theme.spacing(1),
    '&& .MuiListItemText-primary': {
      fontSize: 16,
      display: ({ isNavOpen }) => (isNavOpen ? 'inline-block' : 'none')
    }
  },
  listItemTextTopLevel: {
    whiteSpace: ({ isNavOpen }) => (isNavOpen ? 'normal' : 'nowrap'),
    '&& .MuiListItemText-primary': {
      fontSize: 16,
      display: ({ isNavOpen }) => (isNavOpen ? 'inline-block' : 'none')
    }
  },
  subListItemText: {
    '&& .MuiListItemText-primary': {
      fontSize: 15,
      whiteSpace: ({ isNavOpen }) => (isNavOpen ? 'normal' : 'nowrap'),
      display: ({ isNavOpen }) => (isNavOpen ? 'inline-block' : 'none')
    }
  },
  drawerLink: {
    display: 'flex',
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.dark
    },
    '&.active': {
      color: darken(theme.palette.primary.main, 0.3),
      fontWeight: 'bold',
      '&& span': {
        fontWeight: 'bold'
      }
    }
  },
  listItemIconRoot: {
    minWidth: '1px',

    // TODO: This seems to be breaking the nav
    marginRight: ({ isNavOpen }) =>
      isNavOpen ? theme.spacing(1) : `calc(${theme.spacing(1)} - 35%)`
  },
  activeIcon: {
    color: theme.palette.primary.main
  }
}));
